/**
 * useLevelUp — lightweight web port of the iOS pendingLevelUp queue.
 *
 * iOS source: globalStore.ts (pendingLevelUp / lastSeenLevel /
 * reconcileLevelOnLoad) + usePresentPendingLevelUpOnTabs.ts. The full native
 * flow chains multi-level jumps per focused tab; the PWA v1 keeps it simple:
 * on mount it compares the user's display level (useDisplayLevel — the
 * activation-aware iOS computeCurrentLevel port, NOT the server's hours-only
 * userStats.level) against a localStorage `lastSeenLevel`. If higher, it
 * queues the in-between levels and presents them one at a time. Persistence
 * happens when the celebration actually renders — LevelUpOverlay calls
 * `markLevelAnimationSeen` on mount, mirroring iOS LevelUpScreen — with
 * `dismiss()` as a belt-and-braces second write.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { callWithAuth, getUserInfo } from "@/api/Api";
import { useDisplayLevel } from "@/hooks/useDisplayLevel";

const STORAGE_PREFIX = "parrot.lastSeenLevel";

/**
 * Per-user localStorage key. iOS gates replay on the per-user server-backed
 * `highestEarnedLevel`; on web we namespace the key by user id so switching
 * accounts in the same browser (A lvl 20 → B lvl 3 → back to A) never chains a
 * spurious celebration for every intermediate level. Falls back to the legacy
 * global key when the user id isn't known yet.
 */
function storageKey(userId?: number): string {
  return userId != null ? `${STORAGE_PREFIX}.${userId}` : STORAGE_PREFIX;
}

function readLastSeen(userId?: number): number | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeLastSeen(level: number, userId?: number): void {
  try {
    localStorage.setItem(storageKey(userId), String(level));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

/**
 * Persist a level as "celebration shown". Mirrors iOS
 * `markLevelAnimationSeen` (globalStore.ts) — called by LevelUpOverlay on
 * mount, the moment the user actually sees the animation, so a navigation
 * away (e.g. the /level-up route's Continue) can never replay it. Monotonic:
 * never lowers the persisted value.
 */
export function markLevelAnimationSeen(level: number, userId?: number): void {
  if (!Number.isFinite(level) || level < 1) return;
  const lastSeen = readLastSeen(userId);
  if (lastSeen == null || level > lastSeen) {
    writeLastSeen(level, userId);
  }
}

export interface UseLevelUpResult {
  /** The level to celebrate right now, or null when nothing is pending. */
  pendingLevel: number | null;
  /** Account id for the active user (namespaces persistence), if known. */
  userId?: number;
  /** Call when the user dismisses the current celebration. */
  dismiss: () => void;
}

export function useLevelUp(): UseLevelUpResult {
  const { level: currentLevel, isReady } = useDisplayLevel();
  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  const userId = userInfoQuery.data?.id;
  const [queue, setQueue] = useState<number[]>([]);

  useEffect(() => {
    // Gate on the user id too: until we know whose levels these are, queueing
    // against the wrong (or legacy global) key can chain spurious celebrations
    // when accounts switch in the same browser.
    if (!isReady || currentLevel < 1 || userId == null) return;

    const lastSeen = readLastSeen(userId);

    // First run for this user: don't celebrate retroactively — just baseline.
    if (lastSeen == null) {
      writeLastSeen(currentLevel, userId);
      return;
    }

    if (currentLevel > lastSeen) {
      const pending: number[] = [];
      for (let l = lastSeen + 1; l <= currentLevel; l++) pending.push(l);
      setQueue(pending);
    } else if (currentLevel < lastSeen) {
      // Level went down (data reset) — re-baseline silently.
      writeLastSeen(currentLevel, userId);
    }
  }, [isReady, currentLevel, userId]);

  const dismiss = useCallback(() => {
    setQueue((q) => {
      const [shown, ...rest] = q;
      if (shown != null) writeLastSeen(shown, userId);
      return rest;
    });
  }, [userId]);

  return { pendingLevel: queue[0] ?? null, userId, dismiss };
}
